import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

/** Deterministic split-stone fill, merged into one draw call per heater. */
export function createHeaterStones(
	width: number,
	height: number,
	depth: number,
): THREE.BufferGeometry {
	const columns = Math.max(2, Math.round(width / 0.085));
	const rows = Math.max(2, Math.round(depth / 0.085));
	const layers = Math.max(2, Math.round(height / 0.075));
	const pieces: THREE.BufferGeometry[] = [];
	const base = new THREE.IcosahedronGeometry(1, 1);
	const random = (seed: number) => {
		const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
		return value - Math.floor(value);
	};
	for (let y = 0; y < layers; y++) {
		for (let z = 0; z < rows; z++) {
			for (let x = 0; x < columns; x++) {
				const seed = 1 + x + z * columns + y * columns * rows;
				const geometry = base.clone();
				const positions = geometry.getAttribute("position");
				for (let i = 0; i < positions.count; i++) {
					const px = positions.getX(i),
						py = positions.getY(i),
						pz = positions.getZ(i);
					// Coordinate-based distortion keeps coincident triangle vertices together.
					const radius =
						0.82 + 0.18 * Math.sin(px * 4 + py * 3 + pz * 5 + seed);
					positions.setXYZ(i, px * radius, py * radius, pz * radius);
				}
				geometry.rotateX(random(seed) * Math.PI);
				geometry.rotateY(random(seed + 101) * Math.PI);
				geometry.computeBoundingBox();
				const size = geometry.boundingBox!.getSize(new THREE.Vector3());
				geometry.center();
				const fill = 0.88 + random(seed + 201) * 0.1;
				geometry.scale(
					((width / columns) * fill) / size.x,
					((height / layers) * fill) / size.y,
					((depth / rows) * fill) / size.z,
				);
				geometry.translate(
					-width / 2 +
						((x + 0.5 + (random(seed + 301) - 0.5) * 0.02) * width) / columns,
					-height / 2 + ((y + 0.5) * height) / layers,
					-depth / 2 +
						((z + 0.5 + (random(seed + 401) - 0.5) * 0.02) * depth) / rows,
				);
				geometry.computeVertexNormals();
				const tone = 0.65 + random(seed + 501) * 0.35;
				const colors = new Float32Array(positions.count * 3);
				for (let i = 0; i < positions.count; i++)
					colors.set([tone * 0.96, tone * 0.98, tone], i * 3);
				geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
				pieces.push(geometry);
			}
		}
	}
	base.dispose();
	const merged = mergeGeometries(pieces)!;
	for (const piece of pieces) piece.dispose();
	merged.computeBoundingBox();
	merged.computeBoundingSphere();
	return merged;
}
